import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Shield, ChevronRight, MessageSquare, BarChart3, Brain, CheckCircle, TrendingUp, Play } from "lucide-react";
import { motion } from "framer-motion";
import { SpurwechselLogo } from "@/components/SpurwechselLogo";

const DEMO_CONVERSATION = [
  { role: "assistant", content: "Guten Tag, mein Name ist Dr. Müller. Ich führe heute das psychologische Gespräch mit Ihnen. Erzählen Sie mir bitte zunächst: Was war der Anlass für Ihre MPU?" },
  { role: "user", content: "Ich wurde mit 1,1 Promille am Steuer angehalten. Das war vor etwa einem Jahr." },
  { role: "assistant", content: "Verstehe. Wie kam es dazu, dass Sie an diesem Tag mit Alkohol gefahren sind? Beschreiben Sie mir bitte den konkreten Ablauf." },
  { role: "user", content: "Ich war bei einem Geburtstag und habe gedacht, es geht noch. Im Nachhinein war das ein großer Fehler." },
  { role: "assistant", content: "Sie sagen 'gedacht, es geht noch' — wie haben Sie Ihren Zustand an dem Abend eingeschätzt? Und wie regelmäßig haben Sie damals Alkohol getrunken?" },
];

const DEMO_ANALYSIS = {
  overall_score: 5.5,
  credibility_score: 5, reflection_score: 4, responsibility_score: 6, motivation_score: 5, prevention_score: 3,
  strengths: ["Grundsätzliche Gesprächsbereitschaft", "Anerkennung, dass es ein Fehler war"],
  weaknesses: ["Vage Beschreibung des Vorfalls — keine konkreten Details", "Bagatellisierung durch 'gedacht, es geht noch'", "Fehlende Angaben zum bisherigen Trinkverhalten"],
  improvement_suggestions: ["Konkreter beschreiben: Wann begann der Abend, wie viel wurde getrunken?", "Eigenes Trinkverhalten ehrlich reflektieren — war es ein Einzelfall?", "Rückfallprävention entwickeln: Was tun Sie konkret in ähnlichen Situationen?"],
};

export default function DemoPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0=intro, 1=session, 2=analysis, 3=progress, 4=cta
  const [msgIndex, setMsgIndex] = useState(0);

  const nextMessage = () => {
    if (msgIndex < DEMO_CONVERSATION.length - 1) setMsgIndex(i => i + 1);
    else setStep(2);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="demo-page">
      <nav className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/b2b")}>
            <SpurwechselLogo size="small" />
            <Badge variant="secondary" className="font-body text-xs">Demo</Badge>
          </div>
          <Button className="font-body text-sm bg-primary text-primary-foreground" onClick={() => navigate("/register")} data-testid="demo-register">
            30 Tage kostenlos testen
          </Button>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          {["Gespräch", "Analyse", "Fortschritt", "Starten"].map((label, i) => (
            <div key={i} className="flex-1">
              <div className={`h-1.5 rounded-full mb-1 ${i <= step - 1 ? "bg-primary" : "bg-border"}`} />
              <p className={`font-body text-xs text-center ${i <= step - 1 ? "text-primary font-medium" : "text-muted-foreground"}`}>{label}</p>
            </div>
          ))}
        </div>

        {/* Intro */}
        {step === 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12" data-testid="demo-intro">
            <Brain className="w-16 h-16 text-primary mx-auto mb-6" />
            <h1 className="font-heading text-3xl font-bold mb-4">Erleben Sie Spurwechsel in 3 Minuten</h1>
            <p className="font-body text-muted-foreground mb-8 max-w-lg mx-auto">
              Sehen Sie, wie eine KI-gestützte MPU-Simulation funktioniert, wie die Analyse aussieht und wie Ihre Klienten davon profitieren.
            </p>
            <Button size="lg" className="font-body bg-primary text-primary-foreground" onClick={() => setStep(1)} data-testid="demo-start">
              <Play className="w-4 h-4 mr-2" /> Demo starten
            </Button>
          </motion.div>
        )}

        {/* Session Demo */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="demo-session">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-info flex items-center justify-center text-white font-bold text-sm">DM</div>
              <div><p className="font-body text-sm font-medium">Dr. Müller</p><Badge variant="secondary" className="text-xs font-body">sachlich</Badge></div>
            </div>
            <div className="space-y-4 mb-6">
              {DEMO_CONVERSATION.slice(0, msgIndex + 1).map((msg, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] p-4 ${msg.role === "user" ? "chat-bubble-user" : "chat-bubble-ai"}`}>
                    <p className="font-body text-sm">{msg.content}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <Button className="w-full font-body bg-primary text-primary-foreground" onClick={nextMessage} data-testid="demo-next">
              {msgIndex < DEMO_CONVERSATION.length - 1 ? "Nächste Nachricht" : "Analyse anzeigen"} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </motion.div>
        )}

        {/* Analysis Demo */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="demo-analysis">
            <h2 className="font-heading text-2xl font-bold">KI-Analyse des Gesprächs</h2>
            <Card className="border-border/50">
              <CardContent className="pt-6 text-center">
                <p className="font-heading text-5xl font-bold text-warning">{DEMO_ANALYSIS.overall_score}</p>
                <p className="font-body text-sm text-muted-foreground">/10 Gesamtbewertung</p>
              </CardContent>
            </Card>
            <div className="grid grid-cols-2 gap-4">
              <Card className="border-l-4 border-l-success bg-success/5"><CardContent className="pt-4">
                <p className="font-body text-sm font-semibold text-success mb-2">Stärken</p>
                {DEMO_ANALYSIS.strengths.map((s, i) => <p key={i} className="font-body text-xs text-muted-foreground">- {s}</p>)}
              </CardContent></Card>
              <Card className="border-l-4 border-l-error bg-error/5"><CardContent className="pt-4">
                <p className="font-body text-sm font-semibold text-error mb-2">Schwächen</p>
                {DEMO_ANALYSIS.weaknesses.map((w, i) => <p key={i} className="font-body text-xs text-muted-foreground">- {w}</p>)}
              </CardContent></Card>
            </div>
            <Card className="border-border/50"><CardContent className="pt-4">
              <p className="font-body text-sm font-medium mb-2">Verbesserungsvorschläge</p>
              {DEMO_ANALYSIS.improvement_suggestions.map((s, i) => (
                <div key={i} className="flex items-start gap-2 mb-1"><TrendingUp className="w-3.5 h-3.5 text-info mt-0.5" /><p className="font-body text-xs text-muted-foreground">{s}</p></div>
              ))}
            </CardContent></Card>
            <Button className="w-full font-body bg-primary text-primary-foreground" onClick={() => setStep(3)} data-testid="demo-to-progress">
              Fortschritts-Dashboard sehen <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </motion.div>
        )}

        {/* Progress Demo */}
        {step === 3 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="demo-progress">
            <h2 className="font-heading text-2xl font-bold">So verfolgen Sie den Fortschritt</h2>
            <div className="grid grid-cols-3 gap-4">
              {[{ label: "Sessions", value: "12" }, { label: "Durchschnitt", value: "7.2/10" }, { label: "Bereitschaft", value: "74%" }].map((s, i) => (
                <Card key={i} className="border-border/50"><CardContent className="pt-4 text-center">
                  <p className="font-heading text-2xl font-bold text-primary">{s.value}</p><p className="font-body text-xs text-muted-foreground">{s.label}</p>
                </CardContent></Card>
              ))}
            </div>
            <Card className="border-border/50"><CardContent className="pt-4">
              <p className="font-body text-sm font-medium mb-3">Bewertungsentwicklung (Beispiel)</p>
              <div className="flex items-end gap-2 h-24">
                {[4, 5, 5.5, 6, 5.5, 6.5, 7, 7, 7.5, 7.2, 8, 7.8].map((v, i) => (
                  <div key={i} className="flex-1 bg-primary/20 rounded-t" style={{ height: `${(v / 10) * 100}%` }}>
                    <div className="bg-primary rounded-t h-full" />
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-1"><span className="font-body text-xs text-muted-foreground">Woche 1</span><span className="font-body text-xs text-muted-foreground">Woche 12</span></div>
            </CardContent></Card>
            <p className="font-body text-sm text-muted-foreground text-center">Als Berater sehen Sie diese Daten für jeden Ihrer Klienten — in Echtzeit.</p>
            <Button className="w-full font-body bg-primary text-primary-foreground" onClick={() => setStep(4)} data-testid="demo-to-cta">
              Jetzt kostenlos testen <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </motion.div>
        )}

        {/* CTA */}
        {step === 4 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12" data-testid="demo-cta">
            <CheckCircle className="w-16 h-16 text-success mx-auto mb-6" />
            <h2 className="font-heading text-3xl font-bold mb-4">Das war Spurwechsel in 3 Minuten.</h2>
            <p className="font-body text-muted-foreground mb-8 max-w-lg mx-auto">
              Stellen Sie sich vor, Ihre Klienten trainieren so zwischen Ihren Sitzungen — selbstständig, mit KI-Feedback, und Sie sehen den Fortschritt in Echtzeit.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" className="font-body bg-primary text-primary-foreground" onClick={() => navigate("/register")} data-testid="demo-final-register">
                30 Tage kostenlos testen
              </Button>
              <Button size="lg" variant="outline" className="font-body" onClick={() => navigate("/b2b")} data-testid="demo-back-b2b">
                Preise ansehen
              </Button>
            </div>
            <p className="font-body text-xs text-muted-foreground mt-4">Keine Kreditkarte erforderlich. DSGVO-konform.</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
